import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Group,
  Card,
  SimpleGrid,
  Badge,
  List,
  ThemeIcon,
  Alert,
  Divider,
  Box,
} from '@mantine/core';
import {
  IconBolt,
  IconActivity,
  IconHome,
  IconCalculator,
  IconCheck,
  IconChartLine,
  IconClock,
} from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { Layout } from '../shared/components/Layout';
import { useSEO } from '../shared/hooks/useSEO';
import { getOSDMetadata } from '../utils/osdMetadata';
import { getTariffDescription } from '../data/tariffs/tariffDescriptions';
import { TariffType } from '../types/graphql';

export function TariffG14Page() {
  const osdMetadata = getOSDMetadata('TAURON');
  const tariff = getTariffDescription(TariffType.G14);

  useSEO({
    title: `G14 - Taryfa dynamiczna RCE TAURON 2026 | Automatyzacja Smart Home`,
    description:
      'Taryfa G14 TAURON 2026 to innowacyjna oferta z cenami godzinowymi opartymi ' +
      'na Rynkowej Cenie Energii (RCE). Idealna dla automatyzacji smart home, ' +
      'prosumentów i oszczędności do 30%. Sprawdź czy G14 jest dla Ciebie.',
    canonicalPath: '/TAURON/kalkulatory/podstawowy/G14',
  });

  return (
    <Layout isAuthenticated={false}>
      {/* Hero Section with SEO Keywords */}
      <Box style={{ backgroundColor: 'var(--mantine-color-red-0)', padding: '3rem 0' }}>
        <Container size="xl">
          <Stack gap="md" align="center" ta="center">
            <ThemeIcon size={80} radius="xl" variant="light" color="red">
              <IconActivity size={48} />
            </ThemeIcon>
            <Group gap="sm">
              <Badge color="red" size="lg" variant="filled">
                G14
              </Badge>
              <Badge color="orange" size="md" variant="light">
                NOWOŚĆ 2025
              </Badge>
            </Group>
            <Title order={1} size={42} fw={700}>
              G14 - Taryfa Dynamiczna RCE
            </Title>
            <Text size="lg" c="dimmed" maw={800}>
              Najnowoczesniejsza taryfa z dynamicznymi cenami godzinowymi opartymi na RCE.
              Oszczędz do 30% dzięki automatyzacji smart home.
            </Text>
          </Stack>
        </Container>
      </Box>

      {/* Key Benefits */}
      <Container size="xl" py={60}>
        <Stack gap="xl">
          <Title order={2} mb="md">
            Dlaczego G14 to przyszłość energetyki?
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2, lg: 4 }} spacing="lg">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <ThemeIcon size={40} radius="md" variant="light" color="red">
                <IconClock size={24} />
              </ThemeIcon>
              <Text fw={600} mt="sm">
                Ceny godzinowe 0-23h
              </Text>
              <Text size="sm" c="dimmed" mt="xs">
                Zmieniające się co godzinę na podstawie prognoz RCE z PSE
              </Text>
            </Card>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <ThemeIcon size={40} radius="md" variant="light" color="red">
                <IconBolt size={24} />
              </ThemeIcon>
              <Text fw={600} mt="sm">
                Automatyzacja smart home
              </Text>
              <Text size="sm" c="dimmed" mt="xs">
                Inteligentne sterowanie urządzeniami domowymi 24/7
              </Text>
            </Card>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <ThemeIcon size={40} radius="md" variant="light" color="red">
                <IconHome size={24} />
              </ThemeIcon>
              <Text fw={600} mt="sm">
                Idealna dla prosumentów
              </Text>
              <Text size="sm" c="dimmed" mt="xs">
                Optymalizacja kosztów przy produkcji i zużyciu energii
              </Text>
            </Card>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <ThemeIcon size={40} radius="md" variant="light" color="red">
                <IconCalculator size={24} />
              </ThemeIcon>
              <Text fw={600} mt="sm">
                Przewidywalne rachunki
              </Text>
              <Text size="sm" c="dimmed" mt="xs">
                Średnia stawka z taryfy G11, łatwe planowanie budetu
              </Text>
            </Card>
          </SimpleGrid>
        </Stack>
      </Container>

      {/* Detailed Tariff Information */}
      <Container size="xl" py={60}>
        <Card shadow="md" padding="xl" radius="lg" withBorder>
          <Stack gap="xl">
            <div>
              <Group gap="xs" mb="md">
                <Badge color="red" size="lg" variant="filled">
                  G14
                </Badge>
                <Badge color="orange" size="md" variant="light">
                  NOWOŚĆ 2025
                </Badge>
              </Group>
              <Title order={2}>{tariff.name}</Title>
            </div>

            <Text size="lg" mb="lg">
              {tariff.fullDescription}
            </Text>

            <Divider />

            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
              <div>
                <Text fw={600} mb="sm" c="red">
                  Dla kogo idealna:
                </Text>
                <List
                  spacing="xs"
                  size="sm"
                  icon={
                    <ThemeIcon color="red" size={20} radius="xl">
                      <IconCheck size={12} />
                    </ThemeIcon>
                  }
                >
                  {tariff.bestFor.map((item, index) => (
                    <List.Item key={index}>{item}</List.Item>
                  ))}
                </List>
              </div>

              <div>
                <Text fw={600} mb="sm" c="orange">
                  Wymagania:
                </Text>
                <List
                  spacing="xs"
                  size="sm"
                  icon={
                    <ThemeIcon color="orange" size={20} radius="xl">
                      <IconCheck size={12} />
                    </ThemeIcon>
                  }
                >
                  <List.Item>Wymaga inteligentnego licznika</List.Item>
                  <List.Item>Wymaga systemu automatyzacji smart home</List.Item>
                  <List.Item>Wymaga monitorowania prognoz cen (PSE)</List.Item>
                  <List.Item>Wymaga elastycznego zużycia 24/7</List.Item>
                  <List.Item>Klienci: {osdMetadata.name}</List.Item>
                </List>
              </div>
            </SimpleGrid>

            <Divider />

            <Group justify="space-between" align="flex-start">
              <div>
                <Text size="sm" c="dimmed" mb="xs">
                  Dynamiczne ceny:
                </Text>
                <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
                  {tariff.timeZoneInfo}
                </Text>
              </div>
              <div>
                <Text size="sm" c="dimmed" mb="xs">
                  Potencjalne oszczędności:
                </Text>
                <Text size="lg" fw={700} c="red">
                  {tariff.estimatedSavings}
                </Text>
              </div>
            </Group>

            <Alert icon={<ThemeIcon size={20} color="red"><IconChartLine size={12} /></ThemeIcon>} color="red" variant="light">
              <Text size="sm">
                <strong>Informacja:</strong> Taryfa G14 wymaga dostępu do prognoz
                Rynkowej Cenie Energii (PSE) w dniu poprzedzającym rozliczeniem.
                Ceny godzinowe publikowane są z jednodniowym wyprzedzeniem na dzień następny.
              </Text>
            </Alert>
          </Stack>
        </Card>
      </Container>

      {/* How G14 RCE Works */}
      <Container size="xl" py={60}>
        <Stack gap="xl">
          <div>
            <Title order={2} mb="md">
              Jak działa taryfa G14 RCE?
            </Title>
            <Text c="dimmed">
              Poznaj mechanizm dynamicznych cen godzinowych opartych na Rynkową Cenie Energii.
            </Text>
          </div>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="sm" c="red">
                Prognozy RCE (PSE)
              </Title>
              <Text size="sm" c="dimmed">
                Polskie Sieci Elektroenergetyczne codziennie publikują prognozy
                Rynkowej Cenie Energii (RCE) na następny dzień. Są one bazą dla
                obliczenia cen w taryfie G14.
              </Text>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="sm" c="red">
                Ceny godzinowe 0-23h
              </Title>
              <Text size="sm" c="dimmed">
                Każda godzina doby ma inną cenę, zależną od prognozu RCE dla tej
                godziny. Inteligentny licznik automatycznie rozlicza energię po odpowiedniej
                stawce godzinowej.
              </Text>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="sm" c="red">
                Automatyzacja smart home
              </Title>
              <Text size="sm" c="dimmed">
                Systemy smart home automatycznie decydują, w których godzinach zużyć
                najwięcej energii (np. noc, weekendy). Urządzenia te włączają się
                same, gdy cena jest niska.
              </Text>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="sm" c="red">
                Średnia stawka bazowa
              </Title>
              <Text size="sm" c="dimmed">
                Wszystkie ceny w G14 oparte są na średnią stawkę z taryfy G11.
                Oznacza to, że zawsze płacisz co najmniej, nigdy nie wyżej niż w G11.
              </Text>
            </Card>
          </SimpleGrid>

          <Card shadow="md" padding="lg" radius="md" withBorder>
            <Title order={3} mb="sm">
              Przykład cen godzinowych
            </Title>
            <Alert color="blue" variant="light">
              <Text size="md" fw={700}>
                ⚡ Przykładowe ceny RCE:
              </Text>
              <Text size="sm" c="dimmed">
                Ceny mogą zmieniać się każdego dnia na podstawie prognozu RCE.
                Poniżej prognozy będą dostępne w dniu poprzedzającym rozliczeniem.
              </Text>
            </Alert>
            <Text size="sm" mt="md" c="dimmed">
              Przykładowe ceny godzinowe dla G14:
            </Text>
            <SimpleGrid cols={{ base: 1, md: 3 }} spacing="sm">
              <Card padding="md" radius="md" withBorder style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
                <Text fw={600} mb="xs" size="sm">0:00-6:00</Text>
                <Text fw={700} c="blue" size="lg">1,25 zł/kWh</Text>
              </Card>
              <Card padding="md" radius="md" withBorder style={{ backgroundColor: 'var(--mantine-color-green-0)' }}>
                <Text fw={600} mb="xs" size="sm">7:00-22:00</Text>
                <Text fw={700} c="green" size="lg">0,80 zł/kWh</Text>
              </Card>
              <Card padding="md" radius="md" withBorder style={{ backgroundColor: 'var(--mantine-color-yellow-0)' }}>
                <Text fw={600} mb="xs" size="sm">Pozostałe</Text>
                <Text fw={700} c="yellow" size="lg">~0,95 zł/kWh</Text>
              </Card>
            </SimpleGrid>
          </Card>
        </Stack>
      </Container>

      {/* Comparison with Other Tariffs */}
      <Container size="xl" py={60}>
        <Stack gap="xl">
          <div>
            <Title order={2} mb="md">
              Porównanie G14 z innymi taryfami TAURON
            </Title>
            <Text c="dimmed">
              Zobacz, jak G14 wypada na tle innych taryf energetycznych.
            </Text>
          </div>

          <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="lg">
            <Card shadow="sm" padding="lg" radius="md" withBorder style={{ opacity: 0.7 }}>
              <Title order={4} mb="sm">
                G11 - Jednostrefowa
              </Title>
              <Text size="sm" c="dimmed">
                Jedna stała stawka 24h. Brak możliwości oszczędności.
              </Text>
              <Text size="lg" fw={700} mt="md" c="gray">
                Brak oszczędności
              </Text>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder style={{ opacity: 0.8 }}>
              <Title order={4} mb="sm">
                G12 - Dwustrefowa
              </Title>
              <Text size="sm" c="dimmed">
                Tańsza noc (22:00-6:00). Oszczędz do 20%.
              </Text>
              <Text size="lg" fw={700} mt="md" c="blue">
                Do 20%
              </Text>
            </Card>

            <Card shadow="md" padding="lg" radius="md" withBorder style={{ borderWidth: 3, borderColor: 'var(--mantine-color-red-5)' }}>
              <Group gap="xs" mb="sm">
                <Badge color="red" variant="filled">
                  G14 - Dynamiczna RCE
                </Badge>
                <Badge color="orange" variant="light">
                  REKOMENDACJA
                </Badge>
              </Group>
              <Text size="sm" c="dimmed">
                Ceny godzinowe oparte na RCE. Oszczędz do 30% przy
                automatyzacji.
              </Text>
              <Text size="lg" fw={700} mt="md" c="red">
                Do 30%
              </Text>
            </Card>
          </SimpleGrid>
        </Stack>
      </Container>

      {/* How to Get G14 */}
      <Container size="xl" py={60}>
        <Stack gap="xl">
          <div>
            <Title order={2} mb="md">
              Jak zmienić taryfę na G14?
            </Title>
            <Text c="dimmed">
              Proces zmiany taryfy jest prosty i wymaga kilku kroków przygotowania.
            </Text>
          </div>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="sm">
                Krok 1: Sprawdź dostępność
              </Title>
              <Text size="sm" c="dimmed">
                Sprawdź w portalu {osdMetadata.elicznikPortal.name} (
                <a
                  href={osdMetadata.elicznikPortal.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--mantine-color-red-6)' }}
                >
                  {osdMetadata.elicznikPortal.url}
                </a>
                ), czy taryfa G14 jest dostępna w Twoim obszarze. Wymaga
                inteligentnego licznika.
              </Text>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="sm">
                Krok 2: Złóż wniosek
              </Title>
              <Text size="sm" c="dimmed">
                Złóż wniosek o zmianę taryfy na G14 online. Zmiana jest zazwycz
                realizowana w ciągu kilku dni roboczych.
              </Text>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="sm">
                Krok 3: Wymiana licznika
              </Title>
              <Text size="sm" c="dimmed">
                Jeśli nie masz inteligentnego licznika, {osdMetadata.shortName} zainstaluje go
                bezpłatnie. Wymaga to do aktywacji taryfy G14.
              </Text>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="sm">
                Krok 4: Automatyzacja (opcjonalna)
              </Title>
              <Text size="sm" c="dimmed">
                Aby pełni wykorzystać potencjał G14, rozważ instalację systemu
                automatyzacji smart home. Włączanie urządzeń w godzinach z niskimi cenami
                zwiększa oszczędności.
              </Text>
            </Card>
          </SimpleGrid>
        </Stack>
      </Container>

      {/* FAQ */}
      <Container size="xl" py={60}>
        <Stack gap="xl">
          <div>
            <Title order={2} mb="md">
              Często zadawane pytania o G14
            </Title>
          </div>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Text fw={600} mb="sm">
                Czy G14 jest dostępna w całym kraju?
              </Text>
              <Text size="sm" c="dimmed">
                Nie, G14 jest dostępna tylko u TAURON Dystrybucja. Jeśli jesteś
                klientem innego operatora, sprawdź dostępne alternatywy.
              </Text>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Text fw={600} mb="sm">
                Ile mogę zaoszczędzić z G14?
              </Text>
              <Text size="sm" c="dimmed">
                Oszczędności zależą od profilu zużycia, stopnia automatyzacji smart home
                i prognoz RCE. Typowo 20-30% dla gospodarstw z elastycznym zużyciem
                i automatyzacją.
              </Text>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Text fw={600} mb="sm">
                Czy mogę mieć G14 bez automatyzacji?
              </Text>
              <Text size="sm" c="dimmed">
                Tak, ale oszczędności będą mniejsze. Pełny potencjał G14 jest
                wydobyty przy włączaniu urządzeń w godzinach z niskimi cenami.
              </Text>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Text fw={600} mb="sm">
                Jak działają prognozy RCE?
              </Text>
              <Text size="sm" c="dimmed">
                PSE publikuje prognozy na następny dzień o godzinie 14:00. Ceny są
                aktualizowane codziennie i wykorzystywane przez inteligentny licznik przy
                rozliczeniu energii.
              </Text>
            </Card>
          </SimpleGrid>
        </Stack>
      </Container>

      {/* CTA */}
      <Box style={{ backgroundColor: 'var(--mantine-color-red-6)', color: 'white' }} py={60}>
        <Container size="md">
          <Stack gap="xl" align="center" ta="center">
            <ThemeIcon size={80} radius="xl" variant="white" color="red">
              <IconCalculator size={48} />
            </ThemeIcon>
            <Title order={2} c="white">
              Sprawdź, ile zaoszczędzisz z G14
            </Title>
            <Text size="lg" c="white" maw={700}>
              Użyj naszego kalkulatora, aby porównać koszty G14 z innymi taryfami
              na podstawie Twojego rzeczywistego zużycia energii.
            </Text>
            <Button
              size="xl"
              variant="white"
              color="red"
              component={Link}
              to="/TAURON/kalkulatory/podstawowy"
              leftSection={<IconCalculator size={24} />}
            >
              Przejdź do kalkulatora
            </Button>
          </Stack>
        </Container>
      </Box>
    </Layout>
  );
}
